import Hero from './components/Hero';
import Navigation from './components/Navigation';
import Services from './components/Services';
import About from './components/About';
import Gallery from './components/Gallery';
import Testimonials from './components/Testimonials';
import Contact from './components/Contact';
import Footer from './components/Footer';

const siteUrl = import.meta.env.VITE_SITE_URL || 'https://www.kcpdr.com';

const webSiteSchema = {
  "@context": "https://schema.org",
  "@type": "WebSite",
  "name": "KC PDR",
  "url": siteUrl,
  "description": "Professional paintless dent repair in Kansas City. 30+ years experience. Hail damage, dent repair, fleet services.",
  "potentialAction": {
    "@type": "SearchAction",
    "target": {
      "@type": "EntryPoint",
      "urlTemplate": `${siteUrl}/?q={search_term_string}`
    },
    "query-input": "required name=search_term_string"
  }
};

const localBusinessSchema = {
  "@context": "https://schema.org",
  "@type": "AutoRepair",
  "name": "KC PDR",
  "description": "Professional paintless dent repair services in Kansas City metro area. 30+ years of combined experience with hail damage repair, dent repair, rental car repair, and fleet vehicle repair.",
  "url": siteUrl,
  "telephone": "+1-660-342-6164",
  "address": {
    "@type": "PostalAddress",
    "streetAddress": "9240 Northwest 63rd Street",
    "addressLocality": "Parkville",
    "addressRegion": "MO",
    "postalCode": "64152",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "39.1947",
    "longitude": "-94.6822"
  },
  "priceRange": "$$",
  "areaServed": {
    "@type": "City",
    "name": "Kansas City"
  },
  "openingHoursSpecification": [
    {
      "@type": "OpeningHoursSpecification",
      "dayOfWeek": ["Monday", "Tuesday", "Wednesday", "Thursday", "Friday"],
      "opens": "08:00",
      "closes": "17:00"
    }
  ],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Paintless Dent Repair Services",
    "itemListElement": [
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Hail Damage Repair",
          "description": "Restore your vehicle's smooth, flawless finish after a storm. Expert hail damage removal with no paint or fillers."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Dent Repair",
          "description": "Professional dent repair that restores your vehicle's shape and shine without costly paintwork."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Rental Car Repair",
          "description": "Quick, reliable dent repairs before returning your rental — avoid costly rental company fees."
        }
      },
      {
        "@type": "Offer",
        "itemOffered": {
          "@type": "Service",
          "name": "Fleet Vehicle Repair",
          "description": "Fast, reliable fleet repairs for businesses. Volume pricing available."
        }
      }
    ]
  }
};

const faqSchema = {
  "@context": "https://schema.org",
  "@type": "FAQPage",
  "mainEntity": [
    {
      "@type": "Question",
      "name": "How much does paintless dent repair cost?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Pricing varies based on the size, location, and number of dents. We provide free, honest estimates with no hidden fees. Most repairs are more affordable than traditional body shop work."
      }
    },
    {
      "@type": "Question",
      "name": "Does insurance cover paintless dent repair?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Yes, we work with all major insurance companies. PDR is often the preferred method because it's faster and more cost-effective than traditional repair."
      }
    },
    {
      "@type": "Question",
      "name": "How long does paintless dent repair take?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "Most repairs are completed within a few hours to a day, depending on the extent of damage. We offer fast turnaround times to get you back on the road quickly."
      }
    },
    {
      "@type": "Question",
      "name": "What types of damage qualify for PDR?",
      "acceptedAnswer": {
        "@type": "Answer",
        "text": "PDR works best for dents where the paint is intact — hail damage, door dings, minor collision dents, and creases. If the paint is cracked or chipped, traditional repair may be needed."
      }
    }
  ]
};

export default function HomePage() {
  return (
    <div className="bg-charcoal min-h-screen">
      {/* React 19 script hoisting — injected into <head> automatically */}
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(webSiteSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }}
      />
      <script
        type="application/ld+json"
        dangerouslySetInnerHTML={{ __html: JSON.stringify(faqSchema) }}
      />
      <Navigation />
      <main>
        <Hero />
        <Services />
        <About />
        <Gallery />
        <Testimonials />
        <Contact />
      </main>
      <Footer />
    </div>
  );
}
